import { queryOptions, useMutation, useQueries, useQuery } from '@tanstack/react-query'

import { detectDocumentKind, renderDocumentPages } from '@/lib/document-pages'

import { createQueryKeys, queryClient, type ApiError } from '../core'
import type { ExistingTemplateResponseDto, GenerateImageResponseDto, GenerationVersionDto, RegenerateBody, RegenerateResponseDto, UpdateAnalysisBody, VersionStatus } from './generation-versions.schemas'
import { generationVersionsService } from './generation-versions.service'

export const generationVersionsKeys = createQueryKeys('generation-versions')

export const generationVersionsQueries = {
  ofProject: (projectId: string) => queryOptions({ queryKey: generationVersionsKeys.all({ projectId }), queryFn: () => generationVersionsService.list(projectId) }),
}

/**
 * `useGenerationVersions(projectId)`: what the server generated for a project, newest first. Pass `undefined` to hold off.
 *
 * It is asked for ONCE and kept: no refetch when a tab is reopened, a component mounts again or the window is focused, however many
 * components use it. It is fetched again only on purpose: when a background task has finished (`refreshGenerationVersions`), or from a
 * "Try again".
 */
export function useGenerationVersions(projectId: string | undefined) {
  return useQuery({
    ...generationVersionsQueries.ofProject(projectId ?? ''),
    enabled: Boolean(projectId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/** Asks the server again, now. Called when a background task has finished (the upload's processing, a generation), because that is when there is something new. */
export function refreshGenerationVersions(projectId: string) {
  return queryClient.fetchQuery({ ...generationVersionsQueries.ofProject(projectId), staleTime: 0 })
}

/** `useGenerateImage().mutateAsync(projectId)`: starts the design and answers with the task to follow. Never retried by itself. */
export function useGenerateImage() {
  return useMutation<GenerateImageResponseDto, ApiError, string>({ mutationFn: (projectId) => generationVersionsService.generateImage(projectId) })
}

/** `useRegenerate().mutateAsync({ projectId, instructions })`: starts the next version and answers with the task to follow. Never retried by itself. */
export function useRegenerate() {
  return useMutation<RegenerateResponseDto, ApiError, { projectId: string } & RegenerateBody>({ mutationFn: ({ projectId, ...body }) => generationVersionsService.regenerate(projectId, body) })
}

/**
 * `useUpdateAnalysis().mutateAsync({ projectId, versionId, instructionAnalysis?, requirementDocsAnalysis? })`: saves an edited analysis. The
 * list the screens read is updated with what was sent, so nothing is asked for again. Never retried by itself.
 */
export function useUpdateAnalysis() {
  return useMutation<void, ApiError, { projectId: string; versionId: string } & UpdateAnalysisBody>({
    mutationFn: ({ projectId, versionId, ...body }) => generationVersionsService.updateAnalysis(projectId, versionId, body),
    onSuccess: (_result, { projectId, versionId, ...body }) => {
      queryClient.setQueryData<GenerationVersionDto[]>(generationVersionsKeys.all({ projectId }), (list) => list?.map((item) => (item.id === versionId ? { ...item, ...body } : item)))
    },
  })
}

/**
 * `useStartFromExistingTemplate().mutateAsync({ projectId, assetId })`: makes the project's first version from a template kept in a collection.
 * The version is put in the project's list, so the studio has it at once. Never retried by itself.
 */
export function useStartFromExistingTemplate() {
  return useMutation<ExistingTemplateResponseDto, ApiError, { projectId: string; assetId: string }>({
    mutationFn: ({ projectId, ...input }) => generationVersionsService.createFromExistingTemplate(projectId, input),
    onSuccess: ({ generationVersion: version }, { projectId }) => {
      queryClient.setQueryData<GenerationVersionDto[]>(generationVersionsKeys.all({ projectId }), (list) => [version, ...(list ?? []).filter((item) => item.id !== version.id)])
    },
  })
}

/**
 * `useSetVersionStatus().mutateAsync({ projectId, versionId, status })`: sends a version for review, or records the client's decision on it.
 * The server decides what that does to the other versions (only one is in review), so the project's versions are asked for again after.
 * Never retried by itself.
 */
export function useSetVersionStatus() {
  return useMutation<void, ApiError, { projectId: string; versionId: string; status: VersionStatus }>({
    mutationFn: ({ projectId, versionId, status }) => generationVersionsService.updateStatus(projectId, versionId, { status }),
    onSuccess: (_result, { projectId, versionId, status }) => {
      queryClient.setQueryData<GenerationVersionDto[]>(generationVersionsKeys.all({ projectId }), (list) => list?.map((item) => (item.id === versionId ? { ...item, status } : item)))
      return queryClient.invalidateQueries({ queryKey: generationVersionsKeys.all({ projectId }) })
    },
  })
}

/**
 * A generated picture as a URL a browser can show. The version only names where the file is stored (`generatedImageUrls` are paths), so
 * the file is downloaded once and turned into an object URL, which stays valid for as long as the page is open and is shared by
 * everything that asks for the same path. The URL is kept for good on purpose: pictures in the project's other screens point at it.
 */
const IMAGE_TYPES: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml', avif: 'image/avif' }

export interface GeneratedImageFile {
  /** An address the browser can show or download, valid for as long as the page is open. */
  url: string
  /** Bytes. */
  size: number
  /** Such as `image/png`. */
  mimeType: string
}

/**
 * The pictures one stored file stands for. A picture is one page. A PDF or a Word document is drawn page by page in the browser (the server
 * does not convert documents), so a design made from one has a picture for each of its pages, and every screen that shows a design's pages
 * shows them like any other. A file that is none of these cannot be shown and is an error, so the screen offers to try again.
 */
export const generatedPagesQuery = (projectId: string, path: string) =>
  queryOptions({
    queryKey: generationVersionsKeys.detail(`${projectId}:${path}`),
    queryFn: async (): Promise<GeneratedImageFile[]> => {
      const blob = await generationVersionsService.downloadImage(projectId, path)
      const kind = await detectDocumentKind(path, blob)
      if (kind === 'pdf' || kind === 'docx') {
        const pages = await renderDocumentPages(kind, blob)
        if (pages.length === 0) throw new Error('This document has no pages to show.')
        return pages.map((page) => ({ url: URL.createObjectURL(page.blob), size: page.blob.size, mimeType: 'image/png' }))
      }
      // The server hands files over as `application/octet-stream`, so the kind of picture comes from its name (v1-….png) instead.
      const mimeType = blob.type.startsWith('image/') ? blob.type : (IMAGE_TYPES[path.split('.').pop()?.toLowerCase() ?? ''] ?? blob.type) || 'application/octet-stream'
      return [{ url: URL.createObjectURL(blob.type === mimeType ? blob : new Blob([blob], { type: mimeType })), size: blob.size, mimeType }]
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

/** The first picture of a stored file, for a place that shows one picture for it. */
export const generatedImageQuery = (projectId: string, path: string) =>
  queryOptions({
    queryKey: generationVersionsKeys.detail(`${projectId}:${path}:first`),
    queryFn: async (): Promise<GeneratedImageFile> => (await queryClient.ensureQueryData(generatedPagesQuery(projectId, path)))[0]!,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

/** Every page of the files, as addresses a browser can show, in order: a document gives one for each of its pages. */
export async function loadGeneratedImages(projectId: string, paths: readonly string[]): Promise<string[]> {
  const pages = await Promise.all(paths.map((path) => queryClient.fetchQuery(generatedPagesQuery(projectId, path))))
  return pages.flat().map((file) => file.url)
}

export async function loadGeneratedImage(projectId: string, path: string): Promise<string> {
  return (await queryClient.fetchQuery(generatedPagesQuery(projectId, path)))[0]!.url
}

/** The same pictures for a screen that shows them: each one's address, size and type, in order, with whether they are still coming or failed. */
export function useGeneratedImages(projectId: string, paths: readonly string[]) {
  const results = useQueries({ queries: paths.map((path) => generatedPagesQuery(projectId, path)) })
  const failed = results.find((result) => result.isError)
  // A file that is a document counts for each of its pages, so the pictures are every page of every file, in order.
  const files = results.flatMap((result) => result.data ?? [])
  return {
    urls: files.map((file) => file.url),
    files,
    isPending: results.some((result) => result.isPending),
    error: failed?.error ?? null,
    retry: () => results.forEach((result) => result.isError && void result.refetch()),
  }
}
